import { useState, useEffect } from 'react';
import {
  Modal,
  Header,
  Segment,
  Statistic,
  Table,
  Dimmer,
  Loader,
  Tab,
  Icon,
  Message,
  Grid,
  Form,
  Button,
  Dropdown,
  Label,
  Divider
} from 'semantic-ui-react';
import axios from 'axios';
import { buildApiUrl } from '../utils/apiConfig';
import {
  createTrackingKey,
  deleteTrackingKey,
  fetchTrackingKeyPositions,
  fetchTrackingKeySecret,
  fetchTrackingKeys,
  rotateTrackingKey,
  setDefaultTourTrackingKey,
  updateTrackingKey,
} from '../utils/trackingKeysApi';

export default function UserDetailsModal({ open, onClose, getApiHeaders, activeUser, onLogout }) {
  const keycloakBaseUrl = String(import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.qrmaps.me').replace(/\/+$/, '');
  const keycloakRealm = String(import.meta.env.VITE_KEYCLOAK_REALM || 'qrmaps').trim();
  const keycloakPasswordUrl = `${keycloakBaseUrl}/realms/${encodeURIComponent(keycloakRealm)}/account/#/security/signingin`;
  const ownTracksEndpointUrl = buildApiUrl('owntracks/');
  const [apiUsage, setApiUsage] = useState(null);
  const [tileUsage, setTileUsage] = useState(null);
  const [tileUsageLoading, setTileUsageLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [userPreferences, setUserPreferences] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [timezones, setTimezones] = useState([]);
  const [homeTimezone, setHomeTimezone] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isCreatingOperatorInvite, setIsCreatingOperatorInvite] = useState(false);
  const [operatorInviteToken, setOperatorInviteToken] = useState('');
  const [operatorInviteId, setOperatorInviteId] = useState('');
  const [operatorInviteError, setOperatorInviteError] = useState('');
  const [operatorInviteCopied, setOperatorInviteCopied] = useState('');
  const [trackingKeys, setTrackingKeys] = useState([]);
  const [trackingKeyName, setTrackingKeyName] = useState('');
  const [trackingKeySecret, setTrackingKeySecret] = useState(null);
  const [trackingKeyLoading, setTrackingKeyLoading] = useState(false);
  const [trackingKeyError, setTrackingKeyError] = useState('');
  const [trackingKeyNotice, setTrackingKeyNotice] = useState('');
  const [trackingHistoryKeyId, setTrackingHistoryKeyId] = useState('');
  const [trackingHistoryPositions, setTrackingHistoryPositions] = useState([]);
  const [trackingHistoryLoading, setTrackingHistoryLoading] = useState(false);
  const [trackingKeyEditingId, setTrackingKeyEditingId] = useState(null);
  const [trackingKeyEditingName, setTrackingKeyEditingName] = useState('');

  // Form state for preferences
  const [preferencesForm, setPreferencesForm] = useState({
    home_location_name: '',
    home_airport_code: '',
    distance_unit: 'km',
    temperature_unit: 'C',
    speed_unit: 'kmh',
    currency: 'EUR',
    timezone: 'UTC',
    time_format: '24'
  });

  // Function to fetch tile usage for a specific month/year
  const fetchTileUsage = async (month, year) => {
    const headers = await getApiHeaders();
    if (!headers.Authorization) return;

    setTileUsageLoading(true);
    try {
      const tileUsageResponse = await axios({
        url: buildApiUrl(`tile-usage/?month=${month}&year=${year}`),
        method: 'GET',
        headers
      });
      setTileUsage(tileUsageResponse.data);
      // Update selected month/year from response
      if (tileUsageResponse.data.month_number) {
        setSelectedMonth(tileUsageResponse.data.month_number);
      }
      if (tileUsageResponse.data.year) {
        setSelectedYear(tileUsageResponse.data.year);
      }
    } catch (tileErr) {
      console.error('Error fetching tile usage:', tileErr);
    } finally {
      setTileUsageLoading(false);
    }
  };

  // Handle month/year change
  const handleTileMonthChange = (_e, { value }) => {
    const newMonth = value;
    setSelectedMonth(newMonth);
    fetchTileUsage(newMonth, selectedYear);
  };

  const handleTileYearChange = (_e, { value }) => {
    const newYear = value;
    setSelectedYear(newYear);
    fetchTileUsage(selectedMonth, newYear);
  };

  // Fetch API usage and user preferences when modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (!open) return;
      const headers = await getApiHeaders();
      if (!headers.Authorization) return;

      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      try {
        // Fetch API usage
        const usageResponse = await axios({
          url: buildApiUrl('api-usage/'),
          method: 'GET',
          headers
        });
        setApiUsage(usageResponse.data);

        // Fetch tile usage from special API server (current month)
        await fetchTileUsage(selectedMonth, selectedYear);

        // Fetch user preferences
        const prefsResponse = await axios({
          url: buildApiUrl('user-preferences/me/'),
          method: 'GET',
          headers
        });

        const prefs = prefsResponse.data.preferences;
        setUserPreferences(prefs);

        // Initialize form with existing preferences
        setPreferencesForm({
          home_location_name: prefs.home_location_name || '',
          home_airport_code: prefs.home_airport_details?.code || prefs.home_airport?.code || '',
          distance_unit: prefs.distance_unit || 'km',
          temperature_unit: prefs.temperature_unit || 'C',
          speed_unit: prefs.speed_unit || 'kmh',
          currency: prefs.currency || 'EUR',
          timezone: prefs.timezone || 'UTC',
          time_format: prefs.time_format || '24'
        });

        const derivedHomeTimezone = await resolveHomeTimezone(prefs);
        if (derivedHomeTimezone) {
          setHomeTimezone(derivedHomeTimezone);
          setPreferencesForm(prev => ({
            ...prev,
            timezone: prev.timezone && prev.timezone !== 'UTC' ? prev.timezone : derivedHomeTimezone
          }));
        }

        try {
          const keys = await fetchTrackingKeys(getApiHeaders);
          setTrackingKeys(keys);
          const selectableKeys = keys.filter((trackingKey) => trackingKey.is_active);
          const defaultKey = selectableKeys.find((trackingKey) => trackingKey.is_default_for_tours) || selectableKeys[0] || null;
          setTrackingHistoryKeyId(defaultKey ? defaultKey.id : '');
        } catch (trackingError) {
          console.error('Error fetching tracking keys:', trackingError);
        }
      } catch (err) {
        setError('Failed to fetch user data');
        console.error('Error fetching user data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, getApiHeaders]);

  useEffect(() => {
    if (!open) return;
    fetchTimezones();
  }, [open]);

  const fetchTimezones = () => {
    try {
      if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
        const supportedTimezones = Intl.supportedValuesOf('timeZone');
        setTimezones(supportedTimezones);
      } else {
        const commonTimezones = [
          'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome',
          'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna',
          'Europe/Prague', 'Europe/Warsaw', 'Europe/Bucharest', 'Europe/Sofia',
          'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Tokyo'
        ];
        setTimezones(commonTimezones);
      }
    } catch (error) {
      console.error('Error fetching timezones:', error);
      setTimezones(['UTC', 'Europe/London', 'America/New_York']);
    }
  };

  const lookupTimezoneFromCoordinates = async (longitude, latitude) => {
    try {
      const response = await axios({
        url: buildApiUrl('coordinate_lookup/'),
        method: 'post',
        data: { latitude, longitude },
        headers: { ...(await getApiHeaders()) }
      });
      return response.data?.timezone || null;
    } catch (error) {
      console.error('Error looking up timezone:', error);
      return null;
    }
  };

  const resolveHomeTimezone = async (prefs) => {
    const explicitTimezone = prefs?.home_airport_details?.time_zone || prefs?.home_airport?.time_zone;
    if (explicitTimezone) return explicitTimezone;

    const location = prefs?.home_airport_details?.location || prefs?.home_airport?.location;
    if (!Array.isArray(location) || location.length < 2) return null;

    const [longitude, latitude] = location;
    return lookupTimezoneFromCoordinates(longitude, latitude);
  };

  const handlePreferenceChange = (_e, { name, value }) => {
    setPreferencesForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await axios({
        url: buildApiUrl('user-preferences/me/update/'),
        method: 'PATCH',
        headers: {
          ...(await getApiHeaders()),
          'Content-Type': 'application/json'
        },
        data: preferencesForm
      });

      setUserPreferences(response.data.preferences);
      setSuccessMessage('Preferences saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save preferences: ' + (err.response?.data?.error || err.message));
      console.error('Error saving preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (!onLogout) return;
    setIsLoggingOut(true);
    try {
      await onLogout();
    } finally {
      setIsLoggingOut(false);
      onClose?.();
    }
  };

  const handleOpenPasswordSettings = () => {
    if (typeof window === 'undefined') return;
    window.open(keycloakPasswordUrl, '_blank', 'noopener,noreferrer');
  };

  const isUnlimitedPlan = String(activeUser?.subscription?.tier_name || '').toLowerCase().includes('unlimited');

  const copyOperatorInviteValue = async (value, kindLabel) => {
    const text = String(value || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setOperatorInviteCopied(`${kindLabel} copied.`);
      setTimeout(() => setOperatorInviteCopied(''), 1800);
    } catch (copyError) {
      setOperatorInviteError(`Could not copy ${kindLabel.toLowerCase()}. Please copy it manually.`);
    }
  };

  const handleCreateOperatorInvite = async () => {
    setOperatorInviteError('');
    setOperatorInviteCopied('');
    setIsCreatingOperatorInvite(true);
    try {
      const headers = await getApiHeaders();
      if (!headers?.Authorization) {
        setOperatorInviteError('Authentication token is not available. Please sign in again.');
        return;
      }

      const response = await axios({
        url: buildApiUrl('onboarding/invites/'),
        method: 'POST',
        headers,
        data: {
          invite_type: 'corporate_assignment',
          max_uses: 1,
          expires_in_hours: 72,
        },
      });

      const inviteData = response?.data || {};
      setOperatorInviteToken(inviteData.token || '');
      setOperatorInviteId(inviteData.id || '');
    } catch (inviteError) {
      const detail = inviteError?.response?.data?.detail;
      setOperatorInviteError(detail || 'Could not create operator invite. Ensure your role has invite permissions.');
    } finally {
      setIsCreatingOperatorInvite(false);
    }
  };

  const reloadTrackingKeys = async () => {
    const keys = await fetchTrackingKeys(getApiHeaders);
    setTrackingKeys(keys);
    const selectableKeys = keys.filter((trackingKey) => trackingKey.is_active);
    const defaultKey = selectableKeys.find((trackingKey) => trackingKey.is_default_for_tours) || selectableKeys[0] || null;
    setTrackingHistoryKeyId((currentId) => {
      if (currentId && selectableKeys.some((trackingKey) => String(trackingKey.id) === String(currentId))) {
        return currentId;
      }
      return defaultKey ? defaultKey.id : '';
    });
  };

  const handleCreateTrackingKey = async () => {
    const name = trackingKeyName.trim();
    if (!name) {
      setTrackingKeyError('Enter a name for this tracked device.');
      return;
    }
    setTrackingKeyLoading(true);
    setTrackingKeyError('');
    setTrackingKeyNotice('');
    setTrackingKeySecret(null);
    try {
      const created = await createTrackingKey({ name }, getApiHeaders);
      setTrackingKeySecret({ id: created.id, name: created.name, key: created.key });
      setTrackingKeyName('');
      setTrackingKeyNotice('Tracking key created.');
      await reloadTrackingKeys();
    } catch (createError) {
      setTrackingKeyError(createError?.response?.data?.detail || createError?.response?.data?.name?.[0] || 'Could not create tracking key.');
    } finally {
      setTrackingKeyLoading(false);
    }
  };

  const handleRevealTrackingKey = async (trackingKey) => {
    setTrackingKeyLoading(true);
    setTrackingKeyError('');
    setTrackingKeyNotice('');
    try {
      const secret = await fetchTrackingKeySecret(trackingKey.id, getApiHeaders);
      setTrackingKeySecret({ id: trackingKey.id, name: trackingKey.name, key: secret.key });
    } catch (secretError) {
      setTrackingKeyError(secretError?.response?.data?.error || 'Could not reveal this tracking key.');
    } finally {
      setTrackingKeyLoading(false);
    }
  };

  const handleRotateTrackingKey = async (trackingKey) => {
    setTrackingKeyLoading(true);
    setTrackingKeyError('');
    setTrackingKeyNotice('');
    try {
      const rotated = await rotateTrackingKey(trackingKey.id, getApiHeaders);
      setTrackingKeySecret({ id: trackingKey.id, name: trackingKey.name, key: rotated.key });
      setTrackingKeyNotice('Tracking key rotated.');
      await reloadTrackingKeys();
    } catch (rotateError) {
      setTrackingKeyError(rotateError?.response?.data?.detail || 'Could not rotate this tracking key.');
    } finally {
      setTrackingKeyLoading(false);
    }
  };

  const handleSetDefaultTrackingKey = async (trackingKey) => {
    setTrackingKeyLoading(true);
    setTrackingKeyError('');
    setTrackingKeyNotice('');
    try {
      await setDefaultTourTrackingKey(trackingKey.id, getApiHeaders);
      setTrackingKeyNotice('Default tour tracking key updated.');
      await reloadTrackingKeys();
    } catch (defaultError) {
      setTrackingKeyError(defaultError?.response?.data?.detail || 'Could not update the default tracking key.');
    } finally {
      setTrackingKeyLoading(false);
    }
  };

  const beginEditTrackingKey = (trackingKey) => {
    setTrackingKeyEditingId(trackingKey.id);
    setTrackingKeyEditingName(trackingKey.name || '');
    setTrackingKeyError('');
    setTrackingKeyNotice('');
  };

  const cancelEditTrackingKey = () => {
    setTrackingKeyEditingId(null);
    setTrackingKeyEditingName('');
  };

  const handleRenameTrackingKey = async (trackingKey) => {
    const nextName = trackingKeyEditingName.trim();
    if (!nextName) {
      setTrackingKeyError('Device name cannot be empty.');
      return;
    }
    setTrackingKeyLoading(true);
    setTrackingKeyError('');
    setTrackingKeyNotice('');
    try {
      await updateTrackingKey(trackingKey.id, { name: nextName }, getApiHeaders);
      setTrackingKeyNotice('Tracking key renamed.');
      cancelEditTrackingKey();
      await reloadTrackingKeys();
    } catch (renameError) {
      setTrackingKeyError(renameError?.response?.data?.name?.[0] || renameError?.response?.data?.detail || 'Could not rename tracking key.');
    } finally {
      setTrackingKeyLoading(false);
    }
  };

  const handleToggleTrackingKeyActive = async (trackingKey) => {
    setTrackingKeyLoading(true);
    setTrackingKeyError('');
    setTrackingKeyNotice('');
    try {
      await updateTrackingKey(trackingKey.id, { is_active: !trackingKey.is_active }, getApiHeaders);
      setTrackingKeyNotice(trackingKey.is_active ? 'Tracking key disabled.' : 'Tracking key enabled.');
      await reloadTrackingKeys();
    } catch (toggleError) {
      setTrackingKeyError(toggleError?.response?.data?.detail || 'Could not update tracking key status.');
    } finally {
      setTrackingKeyLoading(false);
    }
  };

  const handleDeleteTrackingKey = async (trackingKey) => {
    const confirmed = window.confirm(`Delete live location device "${trackingKey.name}" and its stored positions?`);
    if (!confirmed) return;
    setTrackingKeyLoading(true);
    setTrackingKeyError('');
    setTrackingKeyNotice('');
    try {
      await deleteTrackingKey(trackingKey.id, getApiHeaders);
      setTrackingKeySecret((currentSecret) => (
        currentSecret?.id === trackingKey.id ? null : currentSecret
      ));
      setTrackingHistoryPositions([]);
      setTrackingKeyNotice('Tracking key deleted.');
      await reloadTrackingKeys();
    } catch (deleteError) {
      setTrackingKeyError(deleteError?.response?.data?.detail || 'Could not delete tracking key.');
    } finally {
      setTrackingKeyLoading(false);
    }
  };

  const copyTrackingKey = async () => {
    if (!trackingKeySecret?.key) return;
    try {
      await navigator.clipboard.writeText(trackingKeySecret.key);
      setTrackingKeyNotice('Tracking key copied.');
    } catch {
      setTrackingKeyError('Could not copy tracking key. Please copy it manually.');
    }
  };

  const buildOwnTracksHttpUrl = (key) => {
    try {
      const url = new URL(ownTracksEndpointUrl);
      url.username = 'qrmaps';
      url.password = key;
      return url.toString();
    } catch {
      return ownTracksEndpointUrl;
    }
  };

  const copyOwnTracksHttpUrl = async () => {
    if (!trackingKeySecret?.key) return;
    try {
      await navigator.clipboard.writeText(buildOwnTracksHttpUrl(trackingKeySecret.key));
      setTrackingKeyNotice('OwnTracks HTTP URL copied.');
    } catch {
      setTrackingKeyError('Could not copy the OwnTracks HTTP URL.');
    }
  };

  const loadTrackingHistory = async () => {
    const selectableKeys = trackingKeys.filter((trackingKey) => trackingKey.is_active);
    const keyId = trackingHistoryKeyId || selectableKeys.find((trackingKey) => trackingKey.is_default_for_tours)?.id || selectableKeys[0]?.id;
    if (!keyId) {
      setTrackingKeyError('Create a tracking key first.');
      return;
    }
    setTrackingHistoryLoading(true);
    setTrackingKeyError('');
    try {
      const data = await fetchTrackingKeyPositions(keyId, getApiHeaders, {
        period_days: 7,
        limit: 100,
      });
      setTrackingHistoryPositions(data.positions || []);
    } catch (historyError) {
      setTrackingHistoryPositions([]);
      setTrackingKeyError(historyError?.response?.data?.detail || 'Could not load tracking history.');
    } finally {
      setTrackingHistoryLoading(false);
    }
  };

  const getSubscriptionTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'premium':
        return 'green';
      case 'standard':
        return 'blue';
      case 'basic':
        return 'grey';
      default:
        return 'black';
    }
  };

  const distanceUnitOptions = [
    { key: 'km', text: 'Kilometers (km)', value: 'km' },
    { key: 'mi', text: 'Miles (mi)', value: 'mi' },
    { key: 'nm', text: 'Nautical Miles (nm)', value: 'nm' }
  ];

  const temperatureUnitOptions = [
    { key: 'C', text: 'Celsius (°C)', value: 'C' },
    { key: 'F', text: 'Fahrenheit (°F)', value: 'F' }
  ];

  const speedUnitOptions = [
    { key: 'kmh', text: 'Kilometers per hour (km/h)', value: 'kmh' },
    { key: 'mph', text: 'Miles per hour (mph)', value: 'mph' },
    { key: 'kn', text: 'Knots (kn)', value: 'kn' }
  ];

  const currencyOptions = [
    { key: 'USD', text: 'US Dollar (USD)', value: 'USD' },
    { key: 'EUR', text: 'Euro (EUR)', value: 'EUR' },
    { key: 'GBP', text: 'British Pound (GBP)', value: 'GBP' },
    { key: 'CZK', text: 'Czech Koruna (CZK)', value: 'CZK' },
    { key: 'JPY', text: 'Japanese Yen (JPY)', value: 'JPY' },
    { key: 'CNY', text: 'Chinese Yuan (CNY)', value: 'CNY' }
  ];

  const timeFormatOptions = [
    { key: '24', text: '24-hour', value: '24' },
    { key: '12AM', text: '12-hour (AM/PM)', value: '12AM' },
    { key: '12am', text: '12-hour (am/pm)', value: '12am' }
  ];

  const timezoneOptions = [...new Set([homeTimezone, ...timezones].filter(Boolean))].map(tz => ({
    key: tz,
    text: tz,
    value: tz
  }));

  // User Info Tab
  const userInfoPane = (
    <Tab.Pane>
      <Segment>
        <Header as="h3">
          <Icon name="user circle" />
          <Header.Content>
            Account Information
            <Header.Subheader>Your subscription and account details</Header.Subheader>
          </Header.Content>
        </Header>
        <Divider />

        <Grid columns={2} stackable>
          <Grid.Row>
            <Grid.Column>
              <Statistic size="small">
                <Statistic.Value>
                  <Icon name="user" />
                  {activeUser?.name || 'N/A'}
                </Statistic.Value>
                <Statistic.Label>Username</Statistic.Label>
              </Statistic>
            </Grid.Column>
            <Grid.Column>
              <Statistic size="small">
                <Statistic.Value style={{ color: getSubscriptionTierColor(activeUser?.subscription?.tier_name) }}>
                  <Icon name="star" />
                  {activeUser?.subscription?.tier_name || 'N/A'}
                </Statistic.Value>
                <Statistic.Label>Subscription Tier</Statistic.Label>
              </Statistic>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Statistic size="small">
                <Statistic.Value>
                  <Icon name="calendar" />
                  {activeUser?.subscription?.end_date || 'N/A'}
                </Statistic.Value>
                <Statistic.Label>Expiry Date</Statistic.Label>
              </Statistic>
            </Grid.Column>
            <Grid.Column>
              <Statistic size="small">
                <Statistic.Value>
                  <Icon name={activeUser?.subscription?.is_active ? "check circle" : "times circle"}
                        color={activeUser?.subscription?.is_active ? "green" : "red"} />
                  {activeUser?.subscription?.is_active ? 'Active' : 'Inactive'}
                </Statistic.Value>
                <Statistic.Label>Status</Statistic.Label>
              </Statistic>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Divider />

        <Header as="h4">
          <Icon name="shield alternate" />
          Security
        </Header>
        <p style={{ color: '#475569', marginTop: 0 }}>
          Password changes are managed in Keycloak account settings.
        </p>
        <Button
          icon
          labelPosition="left"
          color="blue"
          onClick={handleOpenPasswordSettings}
        >
          <Icon name="lock" />
          Change Password
        </Button>

        <Divider />

        <Header as="h4">
          <Icon name="key" />
          Subscription Features
        </Header>
        <Grid columns={3} divided>
          <Grid.Row>
            <Grid.Column textAlign="center">
              <Label color={activeUser?.subscription?.can_create_maps ? "green" : "grey"}>
                <Icon name={activeUser?.subscription?.can_create_maps ? "check" : "times"} />
                Create Maps
              </Label>
            </Grid.Column>
            <Grid.Column textAlign="center">
              <Label color={activeUser?.subscription?.can_edit_tours ? "green" : "grey"}>
                <Icon name={activeUser?.subscription?.can_edit_tours ? "check" : "times"} />
                Edit Tours
              </Label>
            </Grid.Column>
            <Grid.Column textAlign="center">
              <Label color={activeUser?.subscription?.can_track_flights ? "green" : "grey"}>
                <Icon name={activeUser?.subscription?.can_track_flights ? "check" : "times"} />
                Track Flights
              </Label>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column textAlign="center">
              <Label color={activeUser?.subscription?.can_view_ships ? "green" : "grey"}>
                <Icon name={activeUser?.subscription?.can_view_ships ? "check" : "times"} />
                View Ships
              </Label>
            </Grid.Column>
            <Grid.Column textAlign="center">
              <Label color={activeUser?.subscription?.can_use_chat ? "green" : "grey"}>
                <Icon name={activeUser?.subscription?.can_use_chat ? "check" : "times"} />
                Use Chat
              </Label>
            </Grid.Column>
            <Grid.Column textAlign="center">
              <Label color={activeUser?.subscription?.can_debug ? "green" : "grey"}>
                <Icon name={activeUser?.subscription?.can_debug ? "check" : "times"} />
                Debug Mode
              </Label>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Divider />

        <Header as="h4">
          <Icon name="database" />
          Usage Limits
        </Header>
        <Statistic.Group size="mini" widths="four">
          <Statistic>
            <Statistic.Value>{activeUser?.subscription?.max_maps || 0}</Statistic.Value>
            <Statistic.Label>Max Maps</Statistic.Label>
          </Statistic>
          <Statistic>
            <Statistic.Value>{activeUser?.subscription?.max_tours || 0}</Statistic.Value>
            <Statistic.Label>Max Tours</Statistic.Label>
          </Statistic>
          <Statistic>
            <Statistic.Value>{activeUser?.subscription?.max_api_calls_per_day || 0}</Statistic.Value>
            <Statistic.Label>Daily API Calls</Statistic.Label>
          </Statistic>
          <Statistic>
            <Statistic.Value>{activeUser?.subscription?.max_storage_mb || 0} MB</Statistic.Value>
            <Statistic.Label>Storage</Statistic.Label>
          </Statistic>
        </Statistic.Group>

        {isUnlimitedPlan ? (
          <>
            <Divider />
            <Header as="h4">
              <Icon name="users" />
              Operator Team Access
            </Header>
            <p style={{ color: '#475569', marginTop: 0 }}>
              Generate a one-time operator onboarding code to invite a team member into your workspace.
            </p>
            <Button
              primary
              icon
              labelPosition="left"
              onClick={handleCreateOperatorInvite}
              loading={isCreatingOperatorInvite}
              disabled={isCreatingOperatorInvite}
            >
              <Icon name="plus" />
              Generate Operator Code
            </Button>

            {operatorInviteError ? (
              <Message negative style={{ marginTop: 12 }}>
                {operatorInviteError}
              </Message>
            ) : null}

            {operatorInviteCopied ? (
              <Message positive style={{ marginTop: 12 }}>
                {operatorInviteCopied}
              </Message>
            ) : null}

            {operatorInviteToken ? (
              <Segment secondary style={{ marginTop: 12 }}>
                <Header as="h5" style={{ marginTop: 0 }}>
                  Latest Operator Invite
                </Header>
                <Form>
                  <Form.Input
                    label="Invite token"
                    readOnly
                    value={operatorInviteToken}
                    action={{
                      color: 'blue',
                      labelPosition: 'right',
                      icon: 'copy',
                      content: 'Copy token',
                      onClick: () => copyOperatorInviteValue(operatorInviteToken, 'Token'),
                    }}
                  />
                  <Form.Input
                    label="Invite link"
                    readOnly
                    value={`${window.location.origin}/onboard/${operatorInviteToken}`}
                    action={{
                      color: 'teal',
                      labelPosition: 'right',
                      icon: 'linkify',
                      content: 'Copy link',
                      onClick: () => copyOperatorInviteValue(`${window.location.origin}/onboard/${operatorInviteToken}`, 'Link'),
                    }}
                  />
                  {operatorInviteId ? (
                    <Label size="small" color="grey">
                      Invite ID: {operatorInviteId}
                    </Label>
                  ) : null}
                </Form>
              </Segment>
            ) : null}
          </>
        ) : null}
      </Segment>
    </Tab.Pane>
  );

  // User Preferences Tab
  const preferencesPane = (
    <Tab.Pane>
      <Segment>
        <Header as="h3">
          <Icon name="settings" />
          <Header.Content>
            User Preferences
            <Header.Subheader>Customize your experience and default settings</Header.Subheader>
          </Header.Content>
        </Header>
        <Divider />

        {successMessage && (
          <Message positive>
            <Icon name="check circle" />
            {successMessage}
          </Message>
        )}

        <Form loading={saving}>
          <Header as="h4">
            <Icon name="home" />
            Home Location
          </Header>
          <Form.Group widths="equal">
            <Form.Input
              fluid
              label="Home Location Name"
              placeholder="e.g., Prague, Czech Republic"
              name="home_location_name"
              value={preferencesForm.home_location_name}
              onChange={handlePreferenceChange}
              icon="map marker alternate"
            />
            <Form.Input
              fluid
              label="Home Airport Code"
              placeholder="e.g., PRG, JFK, LHR"
              name="home_airport_code"
              value={preferencesForm.home_airport_code}
              onChange={handlePreferenceChange}
              icon="plane"
            />
          </Form.Group>

          {(userPreferences?.home_airport_details || userPreferences?.home_airport) && (
            <Message info icon>
              <Icon name="info circle" />
              <Message.Content>
                <Message.Header>Current Home Airport</Message.Header>
                {(userPreferences.home_airport_details || userPreferences.home_airport).name}{' '}
                ({(userPreferences.home_airport_details || userPreferences.home_airport).code})
              </Message.Content>
            </Message>
          )}

          <Divider />

          <Header as="h4">
            <Icon name="tachometer alternate" />
            Unit Preferences
          </Header>
          <Form.Group widths="equal">
            <Form.Field>
              <label>
                <Icon name="arrows alternate horizontal" />
                Distance Unit
              </label>
              <Dropdown
                fluid
                selection
                name="distance_unit"
                options={distanceUnitOptions}
                value={preferencesForm.distance_unit}
                onChange={handlePreferenceChange}
              />
            </Form.Field>
            <Form.Field>
              <label>
                <Icon name="thermometer half" />
                Temperature Unit
              </label>
              <Dropdown
                fluid
                selection
                name="temperature_unit"
                options={temperatureUnitOptions}
                value={preferencesForm.temperature_unit}
                onChange={handlePreferenceChange}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>
                <Icon name="shipping fast" />
                Speed Unit
              </label>
              <Dropdown
                fluid
                selection
                name="speed_unit"
                options={speedUnitOptions}
                value={preferencesForm.speed_unit}
                onChange={handlePreferenceChange}
              />
            </Form.Field>
            <Form.Field>
              <label>
                <Icon name="dollar sign" />
                Currency
              </label>
              <Dropdown
                fluid
                selection
                name="currency"
                options={currencyOptions}
                value={preferencesForm.currency}
                onChange={handlePreferenceChange}
              />
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label>
              <Icon name="clock outline" />
              Timezone
            </label>
            <Dropdown
              fluid
              search
              selection
              placeholder="Select timezone"
              name="timezone"
              options={timezoneOptions}
              value={preferencesForm.timezone}
              onChange={handlePreferenceChange}
            />
          </Form.Field>

          <Form.Field>
            <label>
              <Icon name="clock" />
              Time Format
            </label>
            <Dropdown
              fluid
              selection
              placeholder="Select time format"
              name="time_format"
              options={timeFormatOptions}
              value={preferencesForm.time_format}
              onChange={handlePreferenceChange}
            />
          </Form.Field>

          <Divider />

          <Button
            primary
            onClick={handleSavePreferences}
            disabled={saving}
            icon
            labelPosition="left"
          >
            <Icon name="save" />
            Save Preferences
          </Button>
        </Form>
      </Segment>
    </Tab.Pane>
  );

  // API Usage Tab
  const apiUsagePane = (
    <Tab.Pane>
      {apiUsage && (
        <Segment>
          <Header as="h3">
            <Icon name="chart line" />
            <Header.Content>
              API Usage Statistics
              <Header.Subheader>Your API activity for {apiUsage.month}</Header.Subheader>
            </Header.Content>
          </Header>
          <Divider />

          <Statistic.Group widths="four" size="small">
            <Statistic>
              <Statistic.Value>
                <Icon name="plug" color="blue" />
                {apiUsage.total_calls}
              </Statistic.Value>
              <Statistic.Label>Total Calls</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>
                <Icon name="check circle" color="green" />
                {apiUsage.total_successful}
              </Statistic.Value>
              <Statistic.Label>Successful</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>
                <Icon name="times circle" color="red" />
                {apiUsage.total_failed}
              </Statistic.Value>
              <Statistic.Label>Failed</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>
                <Icon name="percent" color="teal" />
                {apiUsage.overall_success_rate}%
              </Statistic.Value>
              <Statistic.Label>Success Rate</Statistic.Label>
            </Statistic>
          </Statistic.Group>

          <Divider />

          <Header as="h4">
            <Icon name="list" />
            API Breakdown
          </Header>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>API Name</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Total Calls</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Success Rate</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {apiUsage.api_usage.map((api, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <Header as="h5">
                      <Icon name="code" />
                      {api.display_name}
                    </Header>
                  </Table.Cell>
                  <Table.Cell>{api.description}</Table.Cell>
                  <Table.Cell textAlign="center">
                    <Label color="blue">{api.total_calls}</Label>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Label color={api.success_rate >= 90 ? "green" : api.success_rate >= 70 ? "yellow" : "red"}>
                      {api.success_rate}%
                    </Label>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {apiUsage.api_usage.some(api => api.common_errors.length > 0) && (
            <>
              <Divider />
              <Header as="h4">
                <Icon name="exclamation triangle" color="red" />
                Common Errors
              </Header>
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>API</Table.HeaderCell>
                    <Table.HeaderCell>Error Message</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Count</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {apiUsage.api_usage.map((api, apiIndex) => (
                    api.common_errors.map((error, errorIndex) => (
                      <Table.Row key={`${apiIndex}-${errorIndex}`}>
                        <Table.Cell>{api.display_name}</Table.Cell>
                        <Table.Cell>{error.message}</Table.Cell>
                        <Table.Cell textAlign="center">
                          <Label color="red">{error.count}</Label>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ))}
                </Table.Body>
              </Table>
            </>
          )}

          {/* Tile Usage Section */}
          <Divider />
          <Header as="h4">
            <Icon name="map" />
            Map Tile Requests
            <Header.Subheader>Select a month to view tile request statistics</Header.Subheader>
          </Header>

          <Form>
            <Form.Group inline>
              <Form.Field>
                <label>Month</label>
                <Dropdown
                  selection
                  compact
                  options={[
                    { key: 1, text: 'January', value: 1 },
                    { key: 2, text: 'February', value: 2 },
                    { key: 3, text: 'March', value: 3 },
                    { key: 4, text: 'April', value: 4 },
                    { key: 5, text: 'May', value: 5 },
                    { key: 6, text: 'June', value: 6 },
                    { key: 7, text: 'July', value: 7 },
                    { key: 8, text: 'August', value: 8 },
                    { key: 9, text: 'September', value: 9 },
                    { key: 10, text: 'October', value: 10 },
                    { key: 11, text: 'November', value: 11 },
                    { key: 12, text: 'December', value: 12 },
                  ]}
                  value={selectedMonth}
                  onChange={handleTileMonthChange}
                  disabled={tileUsageLoading}
                />
              </Form.Field>
              <Form.Field>
                <label>Year</label>
                <Dropdown
                  selection
                  compact
                  options={Array.from({ length: new Date().getFullYear() - 2019 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return { key: year, text: year.toString(), value: year };
                  })}
                  value={selectedYear}
                  onChange={handleTileYearChange}
                  disabled={tileUsageLoading}
                />
              </Form.Field>
              {tileUsageLoading && <Loader active inline size="small" />}
            </Form.Group>
          </Form>

          {tileUsage && (
            <>
              <Statistic.Group widths="three" size="small" style={{ marginTop: '1em' }}>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="th" color="blue" />
                    {tileUsage.total_tile_requests?.toLocaleString() || 0}
                  </Statistic.Value>
                  <Statistic.Label>Total Tile Requests</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="server" color="teal" />
                    {tileUsage.tile_requests_by_key?.length || 0}
                  </Statistic.Value>
                  <Statistic.Label>Active API Keys</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="wifi" color="purple" />
                    {tileUsage.unique_ips || 0}
                  </Statistic.Value>
                  <Statistic.Label>Unique IPs</Statistic.Label>
                </Statistic>
              </Statistic.Group>

              {tileUsage.tile_requests_by_key?.length > 0 && (
                <>
                  <Divider />
                  <Header as="h5">
                    <Icon name="key" />
                    Requests by API Key
                  </Header>
                  <Table celled striped compact>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Source</Table.HeaderCell>
                        <Table.HeaderCell>Type</Table.HeaderCell>
                        <Table.HeaderCell textAlign="center">Requests</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {tileUsage.tile_requests_by_key.map((item, index) => (
                        <Table.Row key={index}>
                          <Table.Cell>
                            <Icon name={item.source_type === 'map' ? 'map' : item.source_type === 'main_api_key' ? 'user' : 'key'} />
                            {item.source_name}
                          </Table.Cell>
                          <Table.Cell>
                            <Label size="tiny" color={item.source_type === 'map' ? 'blue' : item.source_type === 'main_api_key' ? 'green' : 'grey'}>
                              {item.source_type === 'map' ? 'Map' : item.source_type === 'main_api_key' ? 'Main Key' : 'API Key'}
                            </Label>
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            <Label color="teal">{item.request_count?.toLocaleString()}</Label>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </>
              )}
            </>
          )}
        </Segment>
      )}
    </Tab.Pane>
  );

  const trackingKeysPane = (
    <Tab.Pane>
      <Segment>
        <Header as="h3">
          <Icon name="map marker alternate" />
          <Header.Content>
            Live Location Devices
            <Header.Subheader>Manage OwnTracks keys used for tour current location and alarms</Header.Subheader>
          </Header.Content>
        </Header>
        <Divider />

        {trackingKeyError && <Message negative content={trackingKeyError} />}
        {trackingKeyNotice && <Message positive content={trackingKeyNotice} />}
        {trackingKeySecret?.key && (
          <Message info>
            <Message.Header>{trackingKeySecret.name} tracking key</Message.Header>
            <Form>
              <Form.Input
                readOnly
                value={trackingKeySecret.key}
                action={{
                  icon: 'copy',
                  content: 'Copy',
                  onClick: copyTrackingKey,
                }}
              />
              <Form.Input
                readOnly
                label="OwnTracks HTTP URL"
                value={buildOwnTracksHttpUrl(trackingKeySecret.key)}
                action={{
                  icon: 'copy',
                  content: 'Copy URL',
                  onClick: copyOwnTracksHttpUrl,
                }}
              />
            </Form>
          </Message>
        )}

        <Form loading={trackingKeyLoading}>
          <Form.Group widths="equal">
            <Form.Input
              label="Device name"
              placeholder="My phone"
              value={trackingKeyName}
              onChange={(_e, data) => setTrackingKeyName(data.value)}
            />
            <Form.Field style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button primary icon labelPosition="left" onClick={handleCreateTrackingKey}>
                <Icon name="plus" />
                Create Key
              </Button>
            </Form.Field>
          </Form.Group>
        </Form>

        <Table celled striped compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Key</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Default</Table.HeaderCell>
              <Table.HeaderCell>Last Used</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {trackingKeys.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan="6">
                  No tracking keys yet.
                </Table.Cell>
              </Table.Row>
            ) : trackingKeys.map((trackingKey) => (
              <Table.Row key={trackingKey.id}>
                <Table.Cell>
                  {trackingKeyEditingId === trackingKey.id ? (
                    <Input
                      size="mini"
                      value={trackingKeyEditingName}
                      onChange={(_e, data) => setTrackingKeyEditingName(data.value)}
                    />
                  ) : trackingKey.name}
                </Table.Cell>
                <Table.Cell>••••{trackingKey.key_last4 || ''}</Table.Cell>
                <Table.Cell>
                  {trackingKey.is_active ? (
                    <Label color="blue">Active</Label>
                  ) : (
                    <Label basic>Disabled</Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {trackingKey.is_default_for_tours ? (
                    <Label color="green">Tour default</Label>
                  ) : (
                    <Label basic>Not default</Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {trackingKey.last_used_at ? new Date(trackingKey.last_used_at).toLocaleString() : 'Never'}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {trackingKeyEditingId === trackingKey.id ? (
                    <Button.Group size="tiny">
                      <Button positive onClick={() => handleRenameTrackingKey(trackingKey)} disabled={trackingKeyLoading}>
                        Save
                      </Button>
                      <Button onClick={cancelEditTrackingKey} disabled={trackingKeyLoading}>
                        Cancel
                      </Button>
                    </Button.Group>
                  ) : (
                    <Button.Group size="tiny">
                      <Button onClick={() => beginEditTrackingKey(trackingKey)} disabled={trackingKeyLoading}>
                        Rename
                      </Button>
                      <Button onClick={() => handleRevealTrackingKey(trackingKey)} disabled={trackingKeyLoading || !trackingKey.is_active}>
                        Reveal
                      </Button>
                      <Button onClick={() => handleRotateTrackingKey(trackingKey)} disabled={trackingKeyLoading || !trackingKey.is_active}>
                        Rotate
                      </Button>
                      <Button
                        positive={!trackingKey.is_default_for_tours}
                        disabled={trackingKey.is_default_for_tours || trackingKeyLoading || !trackingKey.is_active}
                        onClick={() => handleSetDefaultTrackingKey(trackingKey)}
                      >
                        Set Default
                      </Button>
                      <Button onClick={() => handleToggleTrackingKeyActive(trackingKey)} disabled={trackingKeyLoading}>
                        {trackingKey.is_active ? 'Disable' : 'Enable'}
                      </Button>
                      <Button negative onClick={() => handleDeleteTrackingKey(trackingKey)} disabled={trackingKeyLoading}>
                        Delete
                      </Button>
                    </Button.Group>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        <Divider />

        <Header as="h4">
          <Icon name="history" />
          Recent Positions
        </Header>
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Device</label>
              <Dropdown
                fluid
                selection
                placeholder="Select device"
                value={trackingHistoryKeyId}
                options={trackingKeys.filter((trackingKey) => trackingKey.is_active).map((trackingKey) => ({
                  key: trackingKey.id,
                  value: trackingKey.id,
                  text: trackingKey.is_default_for_tours ? `${trackingKey.name} (tour default)` : trackingKey.name,
                }))}
                onChange={(_e, data) => setTrackingHistoryKeyId(data.value)}
                disabled={trackingKeys.filter((trackingKey) => trackingKey.is_active).length === 0}
              />
            </Form.Field>
            <Form.Field style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                icon
                labelPosition="left"
                onClick={loadTrackingHistory}
                loading={trackingHistoryLoading}
                disabled={trackingHistoryLoading || trackingKeys.filter((trackingKey) => trackingKey.is_active).length === 0}
              >
                <Icon name="refresh" />
                Load Recent Positions
              </Button>
            </Form.Field>
          </Form.Group>
        </Form>

        <Table celled compact striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Recorded</Table.HeaderCell>
              <Table.HeaderCell>Latitude</Table.HeaderCell>
              <Table.HeaderCell>Longitude</Table.HeaderCell>
              <Table.HeaderCell>Speed</Table.HeaderCell>
              <Table.HeaderCell>Accuracy</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {trackingHistoryPositions.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan="5">
                  No recent positions loaded.
                </Table.Cell>
              </Table.Row>
            ) : trackingHistoryPositions.map((position) => (
              <Table.Row key={position.id}>
                <Table.Cell>{position.recorded_at ? new Date(position.recorded_at).toLocaleString() : 'Unknown'}</Table.Cell>
                <Table.Cell>{Number(position.latitude).toFixed(6)}</Table.Cell>
                <Table.Cell>{Number(position.longitude).toFixed(6)}</Table.Cell>
                <Table.Cell>
                  {Number.isFinite(Number(position.speed_mps)) ? `${(Number(position.speed_mps) * 3.6).toFixed(1)} km/h` : 'N/A'}
                </Table.Cell>
                <Table.Cell>
                  {Number.isFinite(Number(position.accuracy_meters)) ? `${Math.round(Number(position.accuracy_meters))} m` : 'N/A'}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    </Tab.Pane>
  );

  const panes = [
    {
      menuItem: { key: 'info', icon: 'user circle', content: 'Account Info' },
      render: () => userInfoPane,
    },
    {
      menuItem: { key: 'preferences', icon: 'settings', content: 'Preferences' },
      render: () => preferencesPane,
    },
    {
      menuItem: { key: 'tracking', icon: 'map marker alternate', content: 'Live Location' },
      render: () => trackingKeysPane,
    },
    {
      menuItem: { key: 'usage', icon: 'chart line', content: 'API Usage' },
      render: () => apiUsagePane,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      closeIcon
    >
      <Modal.Header>
        <Icon name="user" />
        User Dashboard
        <Button
          floated="right"
          negative
          size="small"
          icon
          labelPosition="left"
          onClick={handleLogout}
          loading={isLoggingOut}
          disabled={!onLogout}
        >
          <Icon name="sign-out" />
          Log out
        </Button>
      </Modal.Header>
      <Modal.Content scrolling style={{ minHeight: '500px' }}>
        <Dimmer active={loading} inverted>
          <Loader>Loading user data...</Loader>
        </Dimmer>

        {error && (
          <Message negative icon>
            <Icon name="exclamation triangle" />
            <Message.Content>
              <Message.Header>Error</Message.Header>
              {error}
            </Message.Content>
          </Message>
        )}

        <Tab
          panes={panes}
          activeIndex={activeTab}
          onTabChange={(_e, { activeIndex }) => setActiveTab(activeIndex)}
          menu={{ secondary: true, pointing: true }}
        />
      </Modal.Content>
    </Modal>
  );
}
